import {
	Avatar,
	Badge,
	Button,
	Group,
	Modal,
	Progress,
	Select,
	SegmentedControl,
	Stack,
	Text,
	TextInput,
	Textarea,
} from '@mantine/core'
import { IconRefresh, IconSend, IconUserSearch } from '@tabler/icons-react'
import dayjs from 'dayjs'
import {
	MantineReactTable,
	MRT_ColumnDef,
	MRT_PaginationState,
} from 'mantine-react-table'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import {
	createSupportInboxCampaign,
	getSupportInboxCampaigns,
	getSupportInboxRecipients,
	getSupportInboxSourceUser,
	searchSupportInboxSourceUsers,
	sendSupportInboxCampaign,
	streamSupportInboxCampaign,
	updateSupportInboxSourceUser,
} from 'src/apis/supportInboxAPI'
import { getListUser } from 'src/apis/userAPI'
import { useModal } from 'src/context/ModalContext'
import { formatDateTime } from 'src/variable/constant'
import classes from './SupportInboxPage.module.scss'

const TARGET_ALL = 'ALL'
const TARGET_SELECTED = 'SELECTED'
const TERMINAL_EVENTS = ['completed', 'failed', 'cancelled']

const statusClassName = (status?: string) => {
	const normalized = String(status || '').toLowerCase()
	if (normalized === 'completed' || normalized === 'sent') {
		return `${classes.statusBadge} ${classes.statusCompleted}`
	}
	if (normalized === 'failed') {
		return `${classes.statusBadge} ${classes.statusFailed}`
	}
	if (normalized === 'queued') {
		return `${classes.statusBadge} ${classes.statusQueued}`
	}
	if (normalized === 'processing') {
		return `${classes.statusBadge} ${classes.statusProcessing}`
	}
	if (normalized === 'pending') {
		return `${classes.statusBadge} ${classes.statusPending}`
	}
	if (normalized === 'cancelled') {
		return `${classes.statusBadge} ${classes.statusCancelled}`
	}
	return `${classes.statusBadge} ${classes.statusDraft}`
}

function SupportInboxPage() {
	const { openError, openModal } = useModal()
	const [sourceUser, setSourceUser] = useState<any>(null)
	const [sourceKeyword, setSourceKeyword] = useState('')
	const [sourceOptions, setSourceOptions] = useState<any[]>([])
	const [selectedSourceUserId, setSelectedSourceUserId] = useState<string | null>(null)

	const [content, setContent] = useState('')
	const [targetType, setTargetType] = useState(TARGET_ALL)
	const [userSearch, setUserSearch] = useState('')
	const [userRows, setUserRows] = useState<any[]>([])
	const [selectedUsers, setSelectedUsers] = useState<Record<string, any>>({})
	const [userLoading, setUserLoading] = useState(false)

	const [campaignRows, setCampaignRows] = useState<any[]>([])
	const [campaignLoading, setCampaignLoading] = useState(false)
	const [campaignSearch, setCampaignSearch] = useState('')
	const [campaignStatus, setCampaignStatus] = useState<string | null>(null)
	const [progress, setProgress] = useState<any>(null)
	const [sending, setSending] = useState(false)

	const [recipientModal, setRecipientModal] = useState<{
		opened: boolean
		campaign: any | null
		rows: any[]
		total: number
		loading: boolean
	}>({
		opened: false,
		campaign: null,
		rows: [],
		total: 0,
		loading: false,
	})
	const [recipientPagination, setRecipientPagination] =
		useState<MRT_PaginationState>({
			pageIndex: 0,
			pageSize: 10,
		})

	const loadSourceUser = useCallback(async () => {
		try {
			const res = await getSupportInboxSourceUser()
			const object = res?.data?.results?.object
			setSourceUser(object?.source_user || null)
			setSelectedSourceUserId(object?.source_user_id || null)
		} catch (error) {
			openError(error)
		}
	}, [openError])

	const loadCampaigns = useCallback(async () => {
		setCampaignLoading(true)
		try {
			const res = await getSupportInboxCampaigns({
				page: 1,
				limit: 20,
				search: campaignSearch || undefined,
				status: campaignStatus || undefined,
			})
			const rows = res?.data?.results?.objects?.rows || []
			setCampaignRows(rows)
		} catch (error) {
			openError(error)
		} finally {
			setCampaignLoading(false)
		}
	}, [campaignSearch, campaignStatus, openError])

	const loadUsers = useCallback(async () => {
		setUserLoading(true)
		try {
			const where: any = { blocked_at: null }
			if (userSearch.trim()) {
				where.name = { $iLike: `%${userSearch.trim()}%` }
			}
			const res = await getListUser({
				fields: ['id', 'name', 'phone', 'email', 'avatar', 'blocked_at', 'created_at'],
				page: 1,
				limit: 10,
				where,
			})
			setUserRows(res?.data?.results?.objects?.rows || [])
		} catch (error) {
			openError(error)
		} finally {
			setUserLoading(false)
		}
	}, [openError, userSearch])

	useEffect(() => {
		loadSourceUser()
		loadCampaigns()
	}, [loadCampaigns, loadSourceUser])

	useEffect(() => {
		if (targetType === TARGET_SELECTED) {
			const timer = setTimeout(loadUsers, 350)
			return () => clearTimeout(timer)
		}
	}, [loadUsers, targetType])

	useEffect(() => {
		const timer = setTimeout(loadCampaigns, 350)
		return () => clearTimeout(timer)
	}, [loadCampaigns])

	useEffect(() => {
		const timer = setTimeout(async () => {
			if (!sourceKeyword.trim()) {
				setSourceOptions([])
				return
			}
			try {
				const res = await searchSupportInboxSourceUsers({
					keyword: sourceKeyword.trim(),
					limit: 20,
				})
				const rows = res?.data?.results?.object || []
				setSourceOptions(
					rows.map((item) => ({
						value: item.id,
						label: `${item.name || 'Unnamed'}${
							item.phone ? ` - ${item.phone}` : ''
						}`,
					})),
				)
			} catch (error) {
				openError(error)
			}
		}, 350)
		return () => clearTimeout(timer)
	}, [openError, sourceKeyword])

	const handleSaveSourceUser = useCallback(async () => {
		if (!selectedSourceUserId) return
		try {
			const res = await updateSupportInboxSourceUser(selectedSourceUserId)
			const object = res?.data?.results?.object
			setSourceUser(object?.source_user || null)
			openModal({
				type: 'success',
				label: 'Support Inbox source user updated successfully',
			})
		} catch (error) {
			openError(error)
		}
	}, [openError, openModal, selectedSourceUserId])

	const handleToggleUser = useCallback((row: any) => {
		setSelectedUsers((prev) => {
			const next = { ...prev }
			if (next[row.id]) {
				delete next[row.id]
			} else {
				next[row.id] = row
			}
			return next
		})
	}, [])

	const handleSend = useCallback(async () => {
		setSending(true)
		try {
			const selectedIds = Object.keys(selectedUsers)
			const createRes = await createSupportInboxCampaign({
				content,
				target_type: targetType,
				...(targetType === TARGET_SELECTED ? { user_ids: selectedIds } : {}),
			})
			const campaign = createRes?.data?.results?.object
			await sendSupportInboxCampaign(campaign.id)
			setProgress({
				id: campaign.id,
				status: 'QUEUED',
				total_count: campaign.total_count || 0,
				success_count: 0,
				failed_count: 0,
			})
			await streamSupportInboxCampaign(campaign.id, ({ event, data }) => {
				setProgress(data)
				if (TERMINAL_EVENTS.includes(event)) {
					loadCampaigns()
				}
			})
			setContent('')
			setSelectedUsers({})
			await loadCampaigns()
		} catch (error) {
			openError(error)
		} finally {
			setSending(false)
		}
	}, [content, loadCampaigns, openError, selectedUsers, targetType])

	const openRecipients = useCallback((campaign: any) => {
		setRecipientPagination({ pageIndex: 0, pageSize: 10 })
		setRecipientModal({
			opened: true,
			campaign,
			rows: [],
			total: 0,
			loading: true,
		})
	}, [])

	const handleRecipientPaginationChange = useCallback(
		(
			updater:
				| MRT_PaginationState
				| ((previous: MRT_PaginationState) => MRT_PaginationState),
		) => {
			setRecipientPagination((previous) => {
				const next =
					typeof updater === 'function' ? updater(previous) : updater
				return next.pageSize === previous.pageSize
					? next
					: { ...next, pageIndex: 0 }
			})
		},
		[],
	)

	useEffect(() => {
		if (!recipientModal.opened || !recipientModal.campaign?.id) return

		let active = true
		const loadRecipients = async () => {
			setRecipientModal((prev) => ({ ...prev, loading: true }))
			try {
				const res = await getSupportInboxRecipients(recipientModal.campaign.id, {
					page: recipientPagination.pageIndex + 1,
					limit: recipientPagination.pageSize,
				})
				if (!active) return

				const objects = res?.data?.results?.objects
				setRecipientModal((prev) => ({
					...prev,
					rows: objects?.rows || [],
					total: Number(objects?.count) || 0,
					loading: false,
				}))
			} catch (error) {
				if (!active) return
				setRecipientModal((prev) => ({ ...prev, loading: false }))
				openError(error)
			}
		}

		loadRecipients()
		return () => {
			active = false
		}
	}, [openError, recipientModal.campaign?.id, recipientModal.opened, recipientPagination])

	const selectedCount = Object.keys(selectedUsers).length
	const progressPercent = progress?.total_count
		? Math.round(
				((Number(progress.success_count || 0) +
					Number(progress.failed_count || 0)) /
					Number(progress.total_count)) *
					100,
		  )
		: 0

	const userColumns = useMemo<MRT_ColumnDef<any>[]>(
		() => [
			{
				accessorKey: 'selected',
				header: '',
				size: 48,
				Cell: ({ row }) => (
					<input
						type="checkbox"
						checked={Boolean(selectedUsers[row.original.id])}
						onChange={() => handleToggleUser(row.original)}
					/>
				),
			},
			{
				accessorKey: 'name',
				header: 'Name',
				Cell: ({ row }) => (
					<Group spacing={8} noWrap>
						<Avatar src={row.original.avatar} radius="xl" size={28} />
						<div>
							<div className={classes.userName}>{row.original.name}</div>
							<div className={classes.muted}>{row.original.email || '-'}</div>
						</div>
					</Group>
				),
			},
			{
				accessorKey: 'phone',
				header: 'Phone',
			},
		],
		[handleToggleUser, selectedUsers],
	)

	const campaignColumns = useMemo<MRT_ColumnDef<any>[]>(
		() => [
			{
				accessorKey: 'content',
				header: 'Content',
				Cell: ({ cell }) => (
					<div className={classes.contentPreview}>
						{cell.getValue<string>()}
					</div>
				),
			},
			{
				accessorKey: 'source_user',
				header: 'Source',
				Cell: ({ row }) => row.original.source_user?.name || '-',
			},
			{
				accessorKey: 'target_type',
				header: 'Target',
			},
			{
				accessorKey: 'status',
				header: 'Status',
				Cell: ({ cell }) => (
					<span className={statusClassName(cell.getValue<string>())}>
						{cell.getValue<string>()}
					</span>
				),
			},
			{
				accessorKey: 'counts',
				header: 'Result',
				Cell: ({ row }) =>
					`${row.original.success_count || 0}/${
						row.original.total_count || 0
					} sent, ${row.original.failed_count || 0} failed`,
			},
			{
				accessorKey: 'created_at',
				header: 'Created at',
				Cell: ({ cell }) =>
					cell.getValue<string>()
						? dayjs(cell.getValue<string>()).format(formatDateTime)
						: '-',
			},
			{
				accessorKey: 'actions',
				header: 'Details',
				Cell: ({ row }) => (
					<Button
						variant="subtle"
						size="xs"
						onClick={() => openRecipients(row.original)}
					>
						Recipients
					</Button>
				),
			},
		],
		[openRecipients],
	)

	const recipientColumns = useMemo<MRT_ColumnDef<any>[]>(
		() => [
			{
				accessorKey: 'user',
				header: 'User',
				Cell: ({ row }) => row.original.user?.name || row.original.user_id,
			},
			{
				accessorKey: 'phone',
				header: 'Phone',
				Cell: ({ row }) => row.original.user?.phone || '-',
			},
			{
				accessorKey: 'status',
				header: 'Status',
				Cell: ({ cell }) => (
					<span className={statusClassName(cell.getValue<string>())}>
						{cell.getValue<string>()}
					</span>
				),
			},
			{
				accessorKey: 'error_message',
				header: 'Error',
			},
		],
		[],
	)

	return (
		<Stack className={classes.page}>
			<div className={classes.header}>
				<div>
					<h1 className={classes.title}>Support Inbox</h1>
					<p className={classes.subtitle}>
						Send support messages from one configured user account.
					</p>
				</div>
				<Button
					leftIcon={<IconRefresh size={16} />}
					variant="light"
					onClick={loadCampaigns}
				>
					Refresh
				</Button>
			</div>

			<div className={classes.layout}>
				<div className={classes.stack}>
					<section className={classes.section}>
						<h2 className={classes.sectionTitle}>Source user</h2>
						<div className={classes.sourceUser}>
							<Avatar src={sourceUser?.avatar} radius="xl" />
							<div>
								<div className={classes.userName}>
									{sourceUser?.name || 'No source user selected'}
								</div>
								<div className={classes.muted}>
									{sourceUser?.phone || sourceUser?.email || '-'}
								</div>
							</div>
						</div>
						<Stack spacing={10} mt={14}>
							<Select
								label="Search by name or phone"
								placeholder="Type to search"
								searchable
								clearable
								icon={<IconUserSearch size={16} />}
								data={sourceOptions}
								searchValue={sourceKeyword}
								onSearchChange={setSourceKeyword}
								value={selectedSourceUserId}
								onChange={setSelectedSourceUserId}
							/>
							<Button onClick={handleSaveSourceUser} disabled={!selectedSourceUserId}>
								Save source user
							</Button>
						</Stack>
					</section>

					<section className={classes.section}>
						<h2 className={classes.sectionTitle}>Composer</h2>
						<Stack spacing={12}>
							<Textarea
								label="Message"
								placeholder="Write support message"
								minRows={5}
								value={content}
								onChange={(event) => setContent(event.currentTarget.value)}
							/>
							<SegmentedControl
								fullWidth
								value={targetType}
								onChange={setTargetType}
								data={[
									{ label: 'All users', value: TARGET_ALL },
									{ label: 'Selected users', value: TARGET_SELECTED },
								]}
							/>
							{targetType === TARGET_SELECTED && (
								<>
									<TextInput
										label="Find users"
										placeholder="Search by name"
										value={userSearch}
										onChange={(event) =>
											setUserSearch(event.currentTarget.value)
										}
									/>
									<Badge color="green" variant="light">
										{selectedCount} selected
									</Badge>
									<MantineReactTable
										columns={userColumns}
										data={userRows}
										state={{ isLoading: userLoading }}
										enableTopToolbar={false}
										enableBottomToolbar={false}
										enableSorting={false}
										enableColumnActions={false}
									/>
								</>
							)}
							<Button
								leftIcon={<IconSend size={16} />}
								loading={sending}
								disabled={
									!content.trim() ||
									!sourceUser ||
									(targetType === TARGET_SELECTED && selectedCount === 0)
								}
								onClick={handleSend}
							>
								Send message
							</Button>
						</Stack>
					</section>
				</div>

				<div className={classes.stack}>
					{progress && (
						<section className={classes.section}>
							<h2 className={classes.sectionTitle}>Progress</h2>
							<Progress value={progressPercent} color="green" />
							<div className={classes.stats}>
								<div className={classes.statBox}>
									<div className={classes.statValue}>
										{progress.total_count || 0}
									</div>
									<div className={classes.statLabel}>Total</div>
								</div>
								<div className={classes.statBox}>
									<div className={classes.statValue}>
										{progress.success_count || 0}
									</div>
									<div className={classes.statLabel}>Sent</div>
								</div>
								<div className={classes.statBox}>
									<div className={classes.statValue}>
										{progress.failed_count || 0}
									</div>
									<div className={classes.statLabel}>Failed</div>
								</div>
							</div>
							<Text mt={10} size="sm" color="dimmed">
								Status: {progress.status}
							</Text>
						</section>
					)}

					<section className={classes.section}>
						<div className={classes.row}>
							<h2 className={classes.sectionTitle}>History</h2>
						</div>
						<Group mb={12} grow>
							<TextInput
								placeholder="Search content"
								value={campaignSearch}
								onChange={(event) =>
									setCampaignSearch(event.currentTarget.value)
								}
							/>
							<Select
								clearable
								placeholder="Status"
								value={campaignStatus}
								onChange={setCampaignStatus}
								data={[
									'DRAFT',
									'QUEUED',
									'PROCESSING',
									'COMPLETED',
									'FAILED',
									'CANCELLED',
								]}
							/>
						</Group>
						<MantineReactTable
							columns={campaignColumns}
							data={campaignRows}
							state={{ isLoading: campaignLoading }}
							enableTopToolbar={false}
							enableBottomToolbar={false}
							enableSorting={false}
							enableColumnActions={false}
						/>
					</section>
				</div>
			</div>

			<Modal
				opened={recipientModal.opened}
				onClose={() => {
					setRecipientPagination({ pageIndex: 0, pageSize: 10 })
					setRecipientModal({
						opened: false,
						campaign: null,
						rows: [],
						total: 0,
						loading: false,
					})
				}}
				size="xl"
				title="Recipient details"
			>
				<MantineReactTable
					columns={recipientColumns}
					data={recipientModal.rows}
					manualPagination
					onPaginationChange={handleRecipientPaginationChange}
					rowCount={recipientModal.total}
					state={{
						isLoading: recipientModal.loading,
						pagination: recipientPagination,
					}}
					enableTopToolbar={false}
					enableSorting={false}
					enableColumnActions={false}
				/>
			</Modal>
		</Stack>
	)
}

export default memo(SupportInboxPage)
